import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import NotificationComposeFields, {
  type NotificationComposeHandle,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/NotificationComposeFields';
import type {
  OrgEvent,
  OrgEventChecklistItemInstance,
} from 'hooks/orgEvents/orgEventHooks';
import { useCreateNotification } from 'hooks/useNotification';
import { isFacilityDemoKind, useTropicalDemoKind } from 'utils/featureFlags';
import { notificationComposeSeedFromChecklist } from './checklistNotifySeed';

type NotifyItem = Extract<
  OrgEventChecklistItemInstance,
  { kind: 'send_notification' }
>;

type ChecklistNotifyComposeModalProps = {
  open: boolean;
  item: NotifyItem | null;
  eventName?: string | null;
  orgEvent?: OrgEvent | null;
  onOpenChange: (open: boolean) => void;
  onSent?: (notificationId: string) => void;
};

function sentNotificationId(): string {
  return `sent-${Date.now()}`;
}

export function ChecklistNotifyComposeModal({
  open,
  item,
  eventName,
  orgEvent,
  onOpenChange,
  onSent,
}: ChecklistNotifyComposeModalProps) {
  const demoKind = useTropicalDemoKind();
  const isEpa = demoKind === 'epa' || isFacilityDemoKind(demoKind);
  const createNotification = useCreateNotification();
  const composeRef = useRef<NotificationComposeHandle>(null);
  const [deliveryMode, setDeliveryMode] = useState<'now' | 'schedule'>('now');
  const resolvedEventName =
    eventName?.trim() || orgEvent?.name?.trim() || 'Event';

  const seed = useMemo(
    () =>
      notificationComposeSeedFromChecklist({
        label: item?.label,
        notify: item?.notify,
        eventName: resolvedEventName,
      }),
    [item, resolvedEventName]
  );

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const collected = composeRef.current?.collect();
    if (!collected || collected.status !== 'ready') return;
    await createNotification.mutateAsync({
      ...collected.fields,
      sourceItemId: isEpa
        ? (orgEvent?.id ?? collected.fields.sourceItemId)
        : collected.fields.sourceItemId,
    });
    onSent?.(sentNotificationId());
    onOpenChange(false);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={item?.label || 'Send Notification'}
      widthClassName="tw:!max-w-5xl tw:sm:!max-w-5xl"
      formProps={{ onSubmit }}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createNotification.isPending}>
            {deliveryMode === 'schedule' ? 'Schedule' : 'Send'}
          </Button>
        </>
      }
    >
      {open ? (
        <NotificationComposeFields
          key={item?.id ?? 'notify'}
          ref={composeRef}
          seed={seed}
          onDeliveryMode={setDeliveryMode}
        />
      ) : null}
    </FormModal>
  );
}
